<template>
  <div>
    <h1 class="text-center">{{ schema.$title }}</h1>

    <school-form :school-model="schema.data.school" @clear="clearStudentData" />

    <panel>
      <div class="row">
        <w-input
          class="col-12 form-group"
          placeholder="0000/0000"
          :model="schema.data.applicationDate"
          :disabled="schema.data.applicationDate.$schema.readOnly"
        />

        <w-radio-list
          class="col-12"
          :model="schema.data.creatorType"
          :values="typeOfCreator"
          :disabled="schema.data.rodo2.$model"
        />

        <div class="col-12 mt-0 form-group">
          <p v-if="schema.data.rodo2.$model" class="small mt-0 mb-0">
            Pole zablokowane gdy została udzielona zgoda na pobranie danych z UONET
          </p>
        </div>

        <yes-or-no class="form-group col-12" :model="schema.data.rodo2" @input="clearStudentData" />

        <w-input class="col-4" :model="schema.data.tenderer.peselInput" @input="tryToGetStudent" />
      </div>
    </panel>

    <panel :title="schema.data.applicant.$title">
      <person
        :model="schema.data.applicant"
        :pesel-model="schema.data.applicant.pesel"
        :disabled="{ address: schema.data.creatorType.$model === 4 }"
      >
        <div class="row form-group">
          <div class="col-12 mb-3">
            <div class="row">
              <yes-or-no
                class="col-6"
                :model="schema.data.applicant.hasPesel"
                :disabled="{ all: !!schema.data.applicant.hasPesel.$schema.readOnly }"
              />

              <gender
                v-if="!schema.data.applicant.gender.$schema.readOnly"
                class="col-6"
                :model="schema.data.applicant.gender"
              />
            </div>
          </div>

          <w-input
            class="col-6"
            :model="schema.data.applicant.pesel"
            :disabled="!!schema.data.applicant.pesel.$schema.readOnly"
          />

          <w-select
            class="col-6 mb-3"
            :model="schema.data.applicant.identificationDocument.type"
            :values="identificationDocumentTypes"
          />

          <w-input class="col-6 mb-3" :model="schema.data.applicant.identificationDocument.number" />

          <w-datepicker
            class="col-6 mb-3"
            :model="schema.data.applicant.birthDate"
            :disabled="!!schema.data.applicant.birthDate.$schema.readOnly"
          />

          <yes-or-no class="form-group col-12" :model="schema.data.rodo">
            <span v-html="schema.data.rodo.$title" />
          </yes-or-no>

          <w-input class="col-12 mb-3" :model="schema.data.applicant.phoneNumber" />
        </div>
      </person>
    </panel>

    <panel :title="schema.data.tenderer.$title">
      <div class="row form-group">
        <w-datepicker
          v-if="schema.data.tenderer.birthDate"
          class="col-4"
          :model="schema.data.tenderer.birthDate"
          :disabled="!!schema.data.tenderer.birthDate.$schema.readOnly"
        />

        <yes-or-no class="col-4" :model="schema.data.tenderer.isFirstGrader" />
      </div>

      <div class="row form-group">
        <w-radio-list class="col-6" :model="schema.data.applicantType" :values="typeOfApplicant" />
      </div>

      <person
        class="applicant"
        :model="schema.data.tenderer"
        :pesel-model="schema.data.tenderer.peselInput"
        :address-title="schema.data.tenderer.address.$title"
        :show-error="{ birthDate: true }"
        :disabled="{
          address: !!schema.data.tenderer.address.$schema.readOnly,
          all: schema.data.creatorType.$model === 1,
          birthDate: !!schema.data.tenderer.birthDate.$schema.readOnly
        }"
      />
    </panel>

    <panel title="Proszę o przyznanie pomocy materialnej o charakterze socjalnym:">
      <slot name="deviationsOrEvents" />
    </panel>

    <panel :title="`Proszę o przyznanie ${benefitType} szkolnego w formie:`">
      <slot name="support" />
    </panel>

    <panel>
      <div class="row">
        <w-input
          class="col-4 form-group"
          tooltip="Liczba osób pozostających we wspólbym gospodarstwie domowym, której dochody (ustalono zgodnie z art. 8 ust. 3-13 ustawy z dnia 12 marca 2004r. o pomocy społecznej - Dz. U. z 2019.1507 j.t.) wskazano we wniosku."
          :model="schema.data.peopleAmount"
        />

        <w-input
          class="col-4 form-group"
          tooltip="Łącznie przez wszystkich członków rodziny wspólnie mieszkających i wspólnie gospodarujących z uczniem/słuchaczem, z miesiąca poprzedzającego złożenie wniosku lub z miesiąca składania  wnisoku w przypadku dochodu utraconego"
          :model="schema.data.totalCosts.totalCost"
        />

        <w-input class="col-4 form-group" :model="schema.data.totalCosts.amountPerPerson" :disabled="true" />
      </div>

      <income-statement :schema="schema" />
    </panel>

    <panel v-if="isScholarship" :title="schema.data.socialAssistanceBenefits.$title">
      <yes-or-no class="form-group" :model="schema.data.socialAssistanceBenefits" title />
    </panel>

    <panel v-if="isScholarship" :title="schema.data.otherScholarship.$title">
      <yes-or-no class="form-group" :model="schema.data.otherScholarship.scholarship" />

      <div v-if="schema.data.otherScholarship.scholarship.$model">
        <w-input class="form-group" :model="schema.data.otherScholarship.scholarshipType" />

        <w-input class="form-group" :model="schema.data.otherScholarship.scholarshipFrom" />

        <w-input class="form-group" :model="schema.data.otherScholarship.scholarshipAmount" />
      </div>
    </panel>

    <panel title="Oświadczenia:">
      <div class="row form-group">
        <div class="col-12">
          <span v-html="schema.data.incomeStatement.$title" />
        </div>
      </div>

      <div v-if="isScholarship" class="row form-group">
        <div class="col-12">
          <span v-html="schema.data.statementAcceptance.$title" />
        </div>
      </div>

      <div class="row form-group">
        <div class="col-12">
          <span v-html="schema.data.personalDataStatement.$title" />
        </div>
      </div>
    </panel>

    <panel :title="schema.data.bankAccountOwner.paymentType.$title">
      <w-radio-list
        class="form-group payment-type"
        :model="schema.data.bankAccountOwner.paymentType"
        :values="typesOfPayment"
        :title="''"
      />

      <w-input
        v-if="schema.$model.data.bankAccountOwner.paymentType === 2"
        :model="schema.data.bankAccountOwner.bankAccountNumber"
      />
    </panel>

    <panel
      v-if="schema.data.termPassedReason && schema._options.external.termPassed"
      :title="schema.data.termPassedReason.$title"
    >
      <div class="row form-group">
        <div class="col-12">
          <w-textarea :model="schema.data.termPassedReason" title />
        </div>
      </div>
    </panel>
  </div>
</template>

<script>
import { components } from '@witkac/bootstrap4-components';

import Panel from '@/components/panel/Panel.vue';
import { Errors } from '@/enums/errors.enum';
import { ow003Mixin } from '@/mixins/document/ow003';
import { setSchoolDataMixin } from '@/mixins/document/setSchoolData';
import { notificationsMixin } from '@/mixins/notifications';
import { replacePlaceholdersMixin } from '@/mixins/replacePlaceholders';
import { serverError } from '@/utils/serverError';

import Gender from '../Gender.vue';
import Person from '../Person.vue';
import SchoolForm from '../SchoolForm.vue';
import IncomeStatement from '../staticComponents/IncomeStatement.vue';
import YesOrNo from '../YesOrNoRadio.vue';

// @vuese
// Formularz wzoru OW-003
export default {
  components: {
    IncomeStatement,
    Gender,
    Panel,
    Person,
    SchoolForm,
    YesOrNo,
    ...components
  },
  mixins: [notificationsMixin, ow003Mixin, replacePlaceholdersMixin, setSchoolDataMixin],
  props: {
    // schema dokumentu
    schema: { type: Object, required: true },
    // dane ucznia
    student: { type: Object, required: false },
    // dane wnioskodawcy
    applicant: { type: Object, required: false },
    // dane szkoły
    school: { type: Object, required: false }
  },
  data() {
    return {
      tenderer: this.schema.$model.data.tenderer
    };
  },
  computed: {
    isScholarship() {
      return this.schema.$model.data.typeOfHelp === 1;
    },
    studentGenders() {
      if (this.schema.$model.data.applicantType === 1) {
        return [
          { value: 1, text: 'Uczeń' },
          { value: 2, text: 'Uczennica' }
        ];
      } else if (this.schema.$model.data.applicantType === 2) {
        return [
          { value: 1, text: 'Słuchacz' },
          { value: 2, text: 'Słuchaczka' }
        ];
      }

      return [
        { value: 1, text: 'Uczeń/Słuchacz' },
        { value: 2, text: 'Uczennica/Słuchaczka' }
      ];
    },
    schoolModel() {
      return this.schema.data.school;
    }
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    // @vuese
    // Wczytuje pobrane dane do wniosku
    loadInitialData() {
      if (this.schema.data.loadedData.$model) {
        return;
      }

      if (this.student) {
        this.schema.data.tenderer.$setData({ ...this.student, address: this.student.address || undefined });
      }

      if (this.applicant) {
        this.schema.data.applicant.$setData({ ...this.applicant, address: this.applicant.address || undefined });
      }

      if (this.student && this.applicant && this.student.pesel === this.applicant.pesel) {
        this.schema.data.creatorType.$setData(1);
      }

      if (this.school) {
        this.updateSchoolData(this.school);
      }

      this.schema.data.loadedData.$setData(true);
    },
    // @vuese
    // Pobiera dane ucznia po peselu
    tryToGetStudent(pesel) {
      if (pesel.length !== 11) {
        return;
      }
      this.$store
        .dispatch('person/listForCurrentUser', { pesel })
        .then(result => {
          if (!result?.list?.length) {
            return this.notifyError(Errors.STUDENT_NOT_FOUND);
          }
          const student = result.list[0];
          this.schema.data.tenderer.$setData({ ...student, peselInput: student.pesel });
          if (this.schema.data.creatorType.$model === 1) {
            this.schema.data.applicant.$setData({ ...student, peselInput: student.pesel });
          }
          if (student.address && this.schema.data.creatorType.$model === 2) {
            this.schema.data.applicant.address.$setData(student.address);
          }
        })
        .catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Czyści dane ucznia po odklikaniu zgody
    clearStudentData() {
      if (this.schema.data.rodo2.$model) {
        return;
      }
      this.schema.data.tenderer.$setDefault();
      if (this.schema.data.creatorType.$model === 1) {
        this.schema.data.applicant.$setDefault();
      } else if (this.schema.data.creatorType.$model === 2) {
        this.schema.data.applicant.address.$setDefault();
      }
    }
  }
};
</script>

<style>
.payment-type .radio {
  display: block;
  margin-bottom: 1rem !important;
}

.applicant .datepicker input:disabled {
  background-color: #f3f3f3;
}
</style>
